import React, { useMemo, useRef } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';
import { palette } from '../../../Convoze.theme';
import { getNipponRatingTone, nipponRatingLegendItems } from './nipponRatingLegend';

const PASS_COLOR = { backgroundColor: '#e7f7ef', color: '#138a4d', borderColor: '#bde8d1' };
const NA_COLOR = { backgroundColor: '#f3f2f8', color: '#66627d', borderColor: '#d8d3eb' };
const NEUTRAL_COLOR = { backgroundColor: '#eef4ff', color: '#4661a8', borderColor: '#cbd6f7' };
const FAIL_COLOR = { backgroundColor: '#fdeeee', color: '#b74b4b', borderColor: '#f2c9c9' };
const RATING_TIER_COLORS = { negative: FAIL_COLOR, neutral: NEUTRAL_COLOR, positive: PASS_COLOR, na: NA_COLOR };

const JOB_CHANGE_WINDOW_YEARS = 3;
const LAVENDER_CELL = '#f4f3fb';
const BORDER_COLOR = '#d9d3ef';
const TEXT_MUTED = '#6d6889';
const COMPETENCY_TOPIC_ORDER = [
  'Sales Challenge',
  'Irate Customer Handling and Cross-Selling',
  'Contest Hustle',
  'Lead Management and Reference Generation',
  'First Impressions',
  'Top Achievement',
  'Team Pride',
  'Stress Management'
];

const M0_TOPIC_PHRASES = {
  'Joining and Launch Readiness': 'joining and launch readiness',
  'First-10-Day Business Volume': 'a first-10-day business volume target',
  'WCP Target Plan': 'a WCP target plan',
  'Natural-Market Preparation': 'natural-market preparation',
  'Prospect and Customer Pipeline': 'an existing prospect/customer pipeline',
  'Product-Learning Plan': 'a product-learning plan',
  'Reference-Generation Strategy': 'a reference-generation strategy for the new role',
  'Hiring Manager Support': 'intent to use hiring-manager support',
  'First-Month Plan Specificity and Practicality': 'a detailed first-month plan'
};

const ONE_PAGER_COMPARISON_LABEL_MAP = {
  Name: 'Candidate Name',
  'City / Location': 'Location',
  'Job Stability in Current Org': 'Current Employer'
};

const isMentioned = (value) => {
  const text = String(value ?? '').trim();
  return Boolean(text) && !['not mentioned', 'unknown', 'n/a'].includes(text.toLowerCase());
};

const sanitizeFileNamePart = (value) => String(value || '')
  .trim()
  .replace(/[^a-zA-Z0-9]+/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_|_$/g, '') || 'candidate';

const normalizePhases = (phases = []) => phases
  .filter(Boolean)
  .map((entry) => {
    const phaseName = Object.keys(entry || {})[0];
    return phaseName ? { phaseName, phase: entry[phaseName] } : null;
  })
  .filter(Boolean);

const normalizeComparison = (comparison) => {
  if (!comparison || typeof comparison !== 'object') return null;
  return {
    status: String(comparison.status || '').trim() || 'pending',
    fields: Array.isArray(comparison.fields) ? comparison.fields : [],
    summary: comparison.summary || {}
  };
};

const buildComparisonLookup = (comparison) => {
  const fields = Array.isArray(comparison?.fields) ? comparison.fields : [];
  return new Map(
    fields
      .map((field) => [String(field?.label || field?.fieldKey || '').trim(), field])
      .filter(([label]) => label)
  );
};

const attachComparison = (rows, comparisonLookup) => rows.map((row) => {
  const comparisonLabel = ONE_PAGER_COMPARISON_LABEL_MAP[row.label];
  const comparisonField = comparisonLabel ? comparisonLookup.get(comparisonLabel) : null;
  return {
    ...row,
    resumeValue: comparisonField?.resumeValue || 'Not Mentioned',
    matchResult: comparisonField?.matchResult || 'N/A'
  };
});

const parseYears = (text) => {
  const match = String(text ?? '').match(/(\d+(\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
};

const computeJobChangesInWindow = (tenureYearsChain, windowYears = JOB_CHANGE_WINDOW_YEARS) => {
  if (tenureYearsChain[0] == null) return null;
  let cumulative = 0;
  let count = 0;
  for (const years of tenureYearsChain) {
    if (years == null) break;
    cumulative += years;
    if (cumulative <= windowYears) count += 1;
  }
  return count;
};

const buildJobStabilitySentence = (entries) => {
  const parts = entries
    .filter(({ tenure }) => isMentioned(tenure))
    .map(({ tenure, employer, isCurrent }) => {
      if (isCurrent) return `${tenure} at current employer${isMentioned(employer) ? ` (${employer})` : ''}`;
      return `${tenure} at ${isMentioned(employer) ? employer : 'a previous employer'}`;
    });
  return parts.length ? parts.join('; ') : 'Not Mentioned';
};

const joinWithOr = (items) => {
  if (!items.length) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
};

const buildM0Summary = (firstName, m0SubParameters) => {
  const addressed = m0SubParameters.filter((sub) => String(sub?.applicability).toLowerCase() === 'applicable' && isMentioned(sub?.evidence) && sub.evidence !== 'No relevant evidence.');
  const notAddressed = m0SubParameters.filter((sub) => !addressed.includes(sub));
  const notAddressedPhrases = notAddressed.map((sub) => M0_TOPIC_PHRASES[sub?.parameter] || (sub?.parameter || '').toLowerCase());

  let text = addressed.length
    ? `${firstName} ${addressed.map((sub) => sub.evidence).join(' ')}`
    : `${firstName} did not provide specifics on launch readiness in the interview.`;

  if (notAddressedPhrases.length) {
    text += ` ${firstName} did not address ${joinWithOr(notAddressedPhrases)}; none of these were raised in the interview.`;
  }
  return text;
};

const formatBusinessValue = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return 'Not Mentioned';
  const normalized = text.toLowerCase();
  if (normalized.includes('last year') || normalized.includes('last 1 year') || normalized.includes('year')) return text;
  return `${text} (total business/premium value cited)`;
};

const getOnePagerData = (normalizedPhases, resumeInterviewComparison = null) => {
  const findPhase = (name) => normalizedPhases.find((item) => item.phaseName === name)?.phase;
  const pdc = findPhase('ProfileDataCollection');
  const ca = findPhase('CompetencyAssessment');
  const pre = findPhase('PeerReferenceExtraction');
  const m0 = findPhase('M0ReadinessAssessment');

  const pdcMap = new Map((pdc?.subParameters || []).map((sub) => [sub?.parameter, sub?.extractedValue]));
  const get = (label) => (isMentioned(pdcMap.get(label)) ? pdcMap.get(label) : 'Not Mentioned');

  const candidateName = get('Candidate Name') !== 'Not Mentioned' ? get('Candidate Name') : 'Candidate';
  const firstName = candidateName.split(' ')[0];

  const currentTenureText = get('Current Tenure');
  const prevTenure1Text = get('Previous Tenure 1');
  const prevTenure2Text = get('Previous Tenure 2');
  const jobChanges = computeJobChangesInWindow([parseYears(currentTenureText), parseYears(prevTenure1Text), parseYears(prevTenure2Text)]);
  const currentTenureYears = parseYears(currentTenureText);
  const comparisonLookup = buildComparisonLookup(normalizeComparison(resumeInterviewComparison));

  const profile = attachComparison([
    { label: 'Name', value: candidateName },
    { label: 'Age', value: get('Age') },
    { label: 'City / Location', value: [get('Location/Area'), get('City')].filter(isMentioned).join(', ') || 'Not Mentioned' },
    { label: 'PAN No.', value: get('PAN Number') },
    { label: 'Industry', value: get('Current Industry') },
    { label: 'Channel (Life Insurance)', value: 'Not Mentioned' },
    { label: 'Current CTC', value: get('Current Annual CTC') }
  ], comparisonLookup);

  const jobChangeText = jobChanges === null
    ? 'Not Mentioned'
    : jobChanges === 0 && currentTenureYears != null && currentTenureYears > JOB_CHANGE_WINDOW_YEARS
      ? `0 in last ${JOB_CHANGE_WINDOW_YEARS} years (has been with current employer for ${currentTenureText})`
      : String(jobChanges);

  const trackRecord = attachComparison([
    {
      label: 'Job Stability in Current Org',
      value: buildJobStabilitySentence([
        { tenure: currentTenureText, employer: get('Current Employer'), isCurrent: true },
        { tenure: prevTenure1Text, employer: get('Previous Company 1'), isCurrent: false },
        { tenure: prevTenure2Text, employer: get('Previous Company 2'), isCurrent: false }
      ])
    },
    { label: `No. of Job Changes in Last ${JOB_CHANGE_WINDOW_YEARS} Years`, value: jobChangeText },
    { label: 'Incentives in Last 1 Year', value: get('Incentive Earned Last Year') },
    { label: 'Promotions in Last 1 Year', value: get('Promotion Last Year') },
    { label: 'International Trips (Last 1 Year)', value: 'Not Mentioned' },
    { label: 'MDRT in Last 2 Years', value: 'Not Mentioned' },
    { label: 'Premium / Business Value (Last 1 Year)', value: formatBusinessValue(get('Business/Premium Value')) }
  ], comparisonLookup);

  const competencyMap = new Map((ca?.subParameters || []).map((sub) => [sub?.parameter, sub]));
  const competencyRows = COMPETENCY_TOPIC_ORDER.map((topic) => {
    const sub = competencyMap.get(topic);
    return {
      topic,
      rating: sub?.rating,
      result: sub?.result
    };
  });

  const numericScores = new Set(nipponRatingLegendItems.map((item) => item.score));
  const ratedValues = competencyRows
    .map((row) => String(row.rating ?? '').trim())
    .filter((score) => numericScores.has(score))
    .map(Number);
  const overallScore = ratedValues.length ? ratedValues.reduce((a, b) => a + b, 0) / ratedValues.length : null;

  const peers = (pre?.subParameters || [])
    .map((sub) => sub?.extractedValue)
    .filter((extracted) => extracted && isMentioned(extracted.name));
  const peerNames = peers.map((peer) => `${peer.name}${isMentioned(peer.currentCompany) ? ` (${peer.currentCompany})` : ''}`).join(', ');

  const m0SubParameters = m0?.subParameters || [];
  const m0Summary = buildM0Summary(firstName, m0SubParameters);

  return {
    candidateName,
    profile,
    trackRecord,
    competencyRows,
    overallScore,
    peerCount: peers.length,
    peerNames,
    m0Summary
  };
};

const SECTION_TITLE_SX = {
  fontWeight: 700,
  color: palette.night,
  fontSize: '1.02rem',
  lineHeight: 1.2,
  borderLeft: `3px solid ${palette.primary}`,
  paddingLeft: '6px'
};

const baseCellSx = {
  border: `1px solid ${BORDER_COLOR}`,
  py: 0.45,
  px: 0.95,
  fontSize: '0.75rem',
  lineHeight: 1.32,
  verticalAlign: 'top'
};

const labelCellSx = {
  ...baseCellSx,
  width: '34%',
  backgroundColor: LAVENDER_CELL,
  fontWeight: 700,
  color: '#65607f'
};

const valueCellSx = {
  ...baseCellSx,
  backgroundColor: '#fff',
  fontWeight: 700,
  color: palette.night
};

const ResultPill = ({ value }) => {
  const normalized = String(value || '').trim().toLowerCase();
  const tone = ['pass', 'true', 'yes'].includes(normalized)
    ? PASS_COLOR
    : ['fail', 'false', 'no'].includes(normalized)
      ? FAIL_COLOR
      : NA_COLOR;
  const label = String(value || '').trim() || 'N/A';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 44,
        px: 0.85,
        py: 0.2,
        borderRadius: 999,
        fontSize: '0.68rem',
        fontWeight: 700,
        lineHeight: 1,
        border: `1px solid ${tone.borderColor}`,
        backgroundColor: tone.backgroundColor,
        color: tone.color
      }}
    >
      {label}
    </Box>
  );
};

const TwoColumnTable = ({ rows }) => {
  const showComparison = rows.some((row) => row.resumeValue !== undefined || row.matchResult !== undefined);
  return (
    <TableContainer sx={{ border: `1px solid ${BORDER_COLOR}`, pageBreakInside: 'avoid', breakInside: 'avoid-page' }}>
      <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' }}>
        {showComparison ? (
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...labelCellSx, width: '28%' }}>Field</TableCell>
              <TableCell sx={{ ...labelCellSx, width: '30%' }}>Interview</TableCell>
              <TableCell sx={{ ...labelCellSx, width: '27%' }}>Resume</TableCell>
              <TableCell sx={{ ...labelCellSx, width: '15%' }}>Match</TableCell>
            </TableRow>
          </TableHead>
        ) : null}
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={labelCellSx}>{row.label}</TableCell>
              <TableCell sx={valueCellSx}>{row.value}</TableCell>
              {showComparison ? (
                <>
                  <TableCell sx={valueCellSx}>{row.resumeValue || 'Not Mentioned'}</TableCell>
                  <TableCell sx={valueCellSx}>
                    <ResultPill value={row.matchResult || 'N/A'} />
                  </TableCell>
                </>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const Section = ({ title, description, children }) => (
  <Box sx={{ mt: 1.25, mb: 0.15, pageBreakInside: 'avoid', breakInside: 'avoid-page' }}>
    <Typography sx={SECTION_TITLE_SX}>
      {title}
      {description ? (
        <Box
          component="span"
          sx={{
            fontSize: '0.64rem',
            fontWeight: 400,
            color: TEXT_MUTED,
            ml: 0.45
          }}
        >
          ({description})
        </Box>
      ) : null}
    </Typography>
    {children}
  </Box>
);

export default function NipponOnePager({ phases = [], reportDateLabel = '', resumeInterviewComparison = null }) {
  const exportRef = useRef(null);
  const normalizedPhases = useMemo(() => normalizePhases(phases), [phases]);
  const data = useMemo(() => getOnePagerData(normalizedPhases, resumeInterviewComparison), [normalizedPhases, resumeInterviewComparison]);

  if (!normalizedPhases.length) return null;

  const pdfFileName = `${sanitizeFileNamePart(data.candidateName)}_OnePager.pdf`;

  const handleExportPdf = async () => {
    if (!exportRef.current) return;

    const sourceNode = exportRef.current;
    const sourceWidth = sourceNode.getBoundingClientRect().width;
    const targetHeight = sourceWidth * (297 / 210);
    const exportWrapper = document.createElement('div');
    const exportClone = sourceNode.cloneNode(true);

    Object.assign(exportWrapper.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      opacity: '0',
      pointerEvents: 'none',
      width: `${sourceWidth}px`,
      height: `${targetHeight}px`,
      overflow: 'hidden',
      background: '#ffffff',
      zIndex: '-1'
    });

    Object.assign(exportClone.style, {
      margin: '0',
      maxWidth: 'none',
      transformOrigin: 'top left',
      boxSizing: 'border-box',
      width: `${sourceWidth}px`
    });

    exportWrapper.appendChild(exportClone);
    document.body.appendChild(exportWrapper);

    const naturalHeight = exportClone.scrollHeight;
    const scale = naturalHeight > targetHeight ? targetHeight / naturalHeight : 1;

    if (scale < 1) {
      exportClone.style.width = `${sourceWidth / scale}px`;
      exportClone.style.transform = `scale(${scale})`;
    }

    const cleanup = () => {
      if (exportWrapper.parentNode) {
        exportWrapper.parentNode.removeChild(exportWrapper);
      }
    };

    try {
      const worker = html2pdf().set({
        margin: [0, 0, 0, 0],
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 1.6, useCORS: true, logging: false }
      }).from(exportClone);

      await worker.toCanvas();
      const canvas = worker.prop.canvas;
      const imageData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      pdf.addImage(imageData, 'JPEG', 0, 0, 210, 297);
      pdf.save(pdfFileName);
    } finally {
      cleanup();
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={handleExportPdf} sx={{ textTransform: 'none' }}>
          Download One-Pager PDF
        </Button>
      </Box>
      <Box
        ref={exportRef}
        sx={{
          backgroundColor: '#fff',
          p: 2.6,
          width: '210mm',
          maxWidth: '210mm',
          boxSizing: 'border-box',
          mx: 'auto',
          fontFamily: 'system-ui, Helvetica, Arial, sans-serif',
          '& table': { tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' },
          '& .MuiTableCell-root': { overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' }
        }}
      >
        <Typography sx={{ fontWeight: 800, color: palette.night, fontSize: '1.7rem', lineHeight: 1.08, mb: 0.22 }}>
          {data.candidateName}
        </Typography>
        <Typography sx={{ color: TEXT_MUTED, fontSize: '0.74rem', mb: 1.05 }}>
          Indusind Nippon Life Insurance Bot Interview - Condensed single-page snapshot{reportDateLabel ? ` - ${reportDateLabel}` : ''}
        </Typography>

        <TwoColumnTable rows={data.profile} />

        <Section
          title="Phase 1 - Profile & Track Record"
          description="Career stability, incentives, and business track-record data points extracted from the interview."
        >
          <TwoColumnTable rows={data.trackRecord} />
        </Section>

        <Section
          title="Phase 2 - Competency Assessment"
          description="Score reflects strength of past examples and results (STAR evidence), on a 1-5 scale."
        >
          <Box sx={{ mb: 0.6 }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.28,
                borderRadius: 999,
                backgroundColor: LAVENDER_CELL,
                color: palette.primary,
                border: `1px solid ${BORDER_COLOR}`,
                fontSize: '0.62rem',
                fontWeight: 700
              }}
            >
              Overall Score: {data.overallScore !== null ? `${data.overallScore.toFixed(1)} / 5` : 'N/A'}
            </Box>
          </Box>
          <TableContainer sx={{ border: `1px solid ${BORDER_COLOR}` }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...labelCellSx, width: '43%' }}>Topic</TableCell>
                  <TableCell sx={{ ...labelCellSx, width: '20%' }}>Rating</TableCell>
                  <TableCell sx={{ ...labelCellSx, width: '13%' }}>Score</TableCell>
                  <TableCell sx={{ ...labelCellSx, width: '24%' }}>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.competencyRows.map((row) => {
                  const tone = getNipponRatingTone(row.rating);
                  const fillColor = RATING_TIER_COLORS[tone.tier]?.color || NA_COLOR.color;
                  return (
                    <TableRow key={row.topic}>
                      <TableCell sx={{ ...valueCellSx, fontWeight: 700 }}>{row.topic}</TableCell>
                      <TableCell sx={valueCellSx}>
                        <Box sx={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }}>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Box
                              key={i}
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                backgroundColor: i < tone.dotCount ? fillColor : '#dad7e8'
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...valueCellSx, fontWeight: 800 }}>
                        {tone.tier === 'na' ? '-' : `${tone.dotCount}/5`}
                      </TableCell>
                      <TableCell sx={valueCellSx}>
                        <ResultPill value={row.result || (tone.tier === 'na' ? 'N/A' : 'Pass')} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Section
          title="Phase 3 - Peer References"
          description="References volunteered by the candidate during the interview."
        >
          <TwoColumnTable
            rows={[
              { label: `References (${data.peerCount})`, value: data.peerCount ? data.peerNames : 'Not Mentioned' }
            ]}
          />
        </Section>

        <Section
          title="Phase 4 - M0 Readiness"
          description="Summary of the candidate's stated joining, launch, and first-month plan."
        >
          <Box sx={{ border: `1px solid ${BORDER_COLOR}`, p: 0.85, minHeight: 76, pageBreakInside: 'avoid', breakInside: 'avoid-page' }}>
            <Typography sx={{ color: palette.night, lineHeight: 1.42, fontSize: '0.74rem', fontWeight: 500 }}>
              {data.m0Summary}
            </Typography>
          </Box>
        </Section>


      </Box>
    </Box>
  );
}





